import Vue from 'vue'

import PrintErrorToast from '@/modules/dispatcher/components/ui/PrintErrorToast.vue'
import { ALLOWED_PRINT_TEMPLATE_IDS } from '@/modules/dispatcher/constants/orders'
import {
    PRINT_GROUP_RESYNC_TIMEOUT_MS,
    PRINT_GROUP_TTL_MS,
    PRINT_STATUS_DELAY_MS,
} from '@/modules/dispatcher/constants/print'
import { getPrintLabels } from '@/modules/dispatcher/constants/printLabels'
import { cleanupExpiredGroups } from '@/modules/dispatcher/utils/printGroupsStorage'
import { fetchPrintGroup } from '@/services/orderService'
import store from '@/store'
import { delay } from '@/utils/delay'
import { notificationService } from '@/utils/notifications'

const printStatusQueue = []
const processedMessages = new Map()
const groupProgress = new Map()
let processingPromise = null
let resyncPromise = null

const showErrorToast = (title, text) => {
    Vue.prototype.$toast(
        {
            component: PrintErrorToast,
            props: {
                variant: 'error',
                title,
                text,
            },
        },
        {
            position: 'top-right',
            timeout: 10000,
            closeButton: 'button',
            closeOnClick: false,
        }
    )
}

const showWarningToast = (title, text) => {
    Vue.prototype.$toast(
        {
            component: PrintErrorToast,
            props: {
                variant: 'warning',
                title,
                text,
            },
        },
        {
            position: 'top-right',
            timeout: 10000,
            closeButton: 'button',
            closeOnClick: false,
        }
    )
}

const checkGroupCompletion = groupId => {
    const isCompleted = store.getters['dispatcher/isGroupCompleted'](groupId)

    if (!isCompleted) return

    const group = store.state.dispatcher.printGroups[groupId]
    const progress = store.getters['dispatcher/getPrintGroupProgress'](groupId)
    const errors = store.getters['dispatcher/getGroupErrors'](groupId)
    const { type } = group
    const labels = getPrintLabels(type)
    const errorList = errors.map(err => `  • ${err}`).join('\n')

    if (type === 'fiscal') {
        const order = group.orders[0]
        const orderNumber = order?.orderNumber || ''

        if (progress.error === 0) {
            notificationService.showSuccess(labels.success(orderNumber))
        } else {
            showErrorToast(
                labels.error,
                labels.errorText(errorList, orderNumber)
            )
        }
    } else if (progress.error === 0) {
        notificationService.showSuccess(labels.success)
    } else if (progress.success === 0) {
        showErrorToast(
            labels.error,
            labels.errorText(errorList, progress.total)
        )
    } else {
        showWarningToast(
            'Печать завершена с ошибками',
            labels.partialText(progress.success, progress.total, errorList)
        )
    }

    group.orders.forEach(order => {
        const messagePrefix = `${group.queueId}_${order.jobId}_`
        processedMessages.forEach((timestamp, key) => {
            if (key.startsWith(messagePrefix)) {
                processedMessages.delete(key)
            }
        })
    })

    store.commit('dispatcher/REMOVE_PRINT_GROUP', groupId)
}

const updatePrintOrderStatus = data => {
    const {
        queue_id: queueId,
        job_id: jobId,
        status,
        error_message: errorMessage,
    } = data.data || data

    if (!queueId || !jobId) {
        console.warn(
            '[WS: Dispatcher] Получены некорректные данные print_status (отсутствует queue_id или job_id)'
        )

        if (import.meta.env.DEV) {
            console.log('[WS: Dispatcher] Сбойный payload:', data)
        }
        return null
    }

    const { printGroups } = store.state.dispatcher
    let targetGroupId = null
    let targetType = ''
    Object.entries(printGroups).forEach(([groupId, group]) => {
        if (group.queueId === queueId) {
            if (group.type === ALLOWED_PRINT_TEMPLATE_IDS.FISCAL) {
                targetGroupId = groupId
            } else {
                const jobMatch = group.orders.find(o => o.jobId === jobId)
                if (jobMatch) {
                    targetGroupId = groupId
                }
            }

            targetType = group.type
        }
    })

    if (!targetGroupId) {
        if (import.meta.env.DEV) {
            console.debug('[WS: Dispatcher] Группа печати не найдена для:', {
                queueId,
                jobId,
            })
        }
        return null
    }

    store.commit('dispatcher/UPDATE_PRINT_ORDER_STATUS', {
        groupId: targetGroupId,
        queueId,
        jobId,
        type: targetType,
        status,
        errorText: errorMessage || null,
    })

    return targetGroupId
}

const processQueue = async () => {
    if (processingPromise) {
        return processingPromise
    }

    processingPromise = (async () => {
        while (printStatusQueue.length > 0) {
            const data = printStatusQueue.shift()
            const {
                queue_id: queueId,
                job_id: jobId,
                status,
            } = data.data || data
            const messageKey = `${queueId}_${jobId}_${status}`

            if (!processedMessages.has(messageKey)) {
                const groupId = updatePrintOrderStatus(data)
                if (groupId) {
                    processedMessages.set(messageKey, Date.now())
                }
                // eslint-disable-next-line no-await-in-loop
                await delay(PRINT_STATUS_DELAY_MS)
                if (groupId) {
                    checkGroupCompletion(groupId)
                }
            }
        }
        processingPromise = null
    })()

    return processingPromise
}

const cleanupExpiredMessages = () => {
    const now = Date.now()
    processedMessages.forEach((timestamp, key) => {
        if (now - timestamp > PRINT_GROUP_TTL_MS) {
            processedMessages.delete(key)
        }
    })
}

export const handleDispatcherPrintEvent = data => {
    if (data?.event !== 'print_status') return

    const {
        queue_id: queueId,
        job_id: jobId,
        status,
    } = data.data || data

    if (!queueId || !jobId || !status) {
        printStatusQueue.push(data)
        processQueue()
        return
    }

    cleanupExpiredMessages()

    const messageKey = `${queueId}_${jobId}_${status}`

    if (processedMessages.has(messageKey)) return

    printStatusQueue.push(data)
    processQueue()
}

const cleanupPrintGroups = () => {
    const { printGroups } = store.state.dispatcher
    const cleanedGroups = cleanupExpiredGroups(printGroups)

    if (Object.keys(cleanedGroups).length !== Object.keys(printGroups).length) {
        store.commit('dispatcher/SET_PRINT_GROUPS', cleanedGroups)
    }
}

export const handleDispatcherPrintGroupEvent = data => {
    const {
        queue_id: queueId,
        total,
        completed,
        failed,
    } = data.data || data

    if (!queueId) {
        console.warn(
            '[WS: Dispatcher] Получены некорректные данные print_group_status (отсутствует queue_id)'
        )
        return
    }

    const targetGroupEntry = Object.entries(
        store.state.dispatcher.printGroups
    ).find(([, group]) => group.queueId === queueId)

    if (!targetGroupEntry) return

    const [groupId, group] = targetGroupEntry
    const labels = getPrintLabels(group.type)
    const successful = completed - failed

    try {
        if (failed === 0) {
            const orderNumber = group.orders[0]?.orderNumber || ''
            const successText =
                typeof labels.success === 'function'
                    ? labels.success(orderNumber)
                    : labels.success
            notificationService.showSuccess(successText)
        } else if (successful === 0) {
            showErrorToast(
                labels.error,
                labels.aggregateText(successful, total, failed)
            )
        } else {
            showWarningToast(
                'Печать завершена с ошибками',
                labels.aggregateText(successful, total, failed)
            )
        }
    } finally {
        groupProgress.delete(groupId)
        let index = printStatusQueue.length
        while (index > 0) {
            index -= 1
            const payload = printStatusQueue[index].data || printStatusQueue[index]
            if (payload.queue_id === queueId) {
                printStatusQueue.splice(index, 1)
            }
        }

        processedMessages.forEach((timestamp, key) => {
            if (key.startsWith(`${queueId}_`)) {
                processedMessages.delete(key)
            }
        })

        store.commit('dispatcher/REMOVE_PRINT_GROUP', groupId)
    }
}

const resyncPrintGroup = async group => {
    try {
        const response = await fetchPrintGroup(group.queueId)
        const progress = response.data
        const targetGroupEntry = Object.entries(
            store.state.dispatcher.printGroups
        ).find(([, item]) => item.queueId === group.queueId)

        if (!targetGroupEntry || progress.queue_id !== group.queueId) return

        const [groupId] = targetGroupEntry
        store.commit('dispatcher/SET_PRINT_GROUP_PROGRESS', {
            groupId,
            progress: {
                total: progress.total,
                completed: progress.completed,
                failed: progress.failed,
                status: progress.status,
            },
        })

        if (
            progress.status !== 'in_progress' ||
            progress.completed >= progress.total
        ) {
            handleDispatcherPrintGroupEvent(progress)
        }
    } catch (error) {
        if (error.response?.status !== 404) {
            console.error(
                `[PrintGroups] Ошибка ресинка группы ${group.queueId}:`,
                error
            )
        }
    }
}

export const resyncDispatcherPrintGroups = async () => {
    if (resyncPromise) return resyncPromise

    cleanupPrintGroups()
    const groups = Object.values(store.state.dispatcher.printGroups)
    resyncPromise = Promise.all(groups.map(resyncPrintGroup)).finally(() => {
        resyncPromise = null
    })
    return resyncPromise
}

export const resyncStalledDispatcherPrintGroups = () => {
    cleanupPrintGroups()
    const groups = store.getters['dispatcher/getPrintGroupsId']
    const activeIds = new Set(groups.map(group => group.id))
    const now = Date.now()

    groupProgress.forEach((value, groupId) => {
        if (!activeIds.has(groupId)) groupProgress.delete(groupId)
    })

    groups.forEach(group => {
        const progress = store.getters['dispatcher/getPrintGroupProgress'](
            group.id
        )
        const currentProgress = progress?.completed || 0
        const previous = groupProgress.get(group.id)

        if (!previous || previous.value !== currentProgress) {
            groupProgress.set(group.id, {
                value: currentProgress,
                updatedAt: now,
            })
            return
        }

        if (now - previous.updatedAt >= PRINT_GROUP_RESYNC_TIMEOUT_MS) {
            previous.updatedAt = now
            resyncPrintGroup(group)
        }
    })
}

export const resetDispatcherPrintEvents = () => {
    printStatusQueue.length = 0
    processedMessages.clear()
    groupProgress.clear()
    processingPromise = null
    resyncPromise = null
}
